import { coerceDeliveryStage } from '@/components/deal/vehicle/delivery-stage/delivery-stage-logic';
import useHubs from '@/hooks/use-hubs';
import useLenders from '@/hooks/use-lenders';
import useUsers from '@/hooks/use-users';
import type { ValueOf } from '@/lib/utils.ts';
import type { Deal } from '@/models/registry';
import { dealStore } from '@/models/stores';
import {
  DeliveryPreferences,
  DeliveryStages,
  FundingMethods,
  Priorities,
  Statuses,
  type User,
} from '@/models/types';
import dayjs from 'dayjs';
import { cloneDeep, isEmpty } from 'lodash';
import { useCallback, useMemo } from 'react';
import {
  CREATED_DATE_FILTER_OPTIONS,
  DELIVERY_DATE_FILTER_OPTIONS,
  FILTER_TYPES,
  type FilterType,
  LAST_UPDATED_DATE_FILTER_OPTIONS,
  PICKUP_DATE_FILTER_OPTIONS,
} from '../types';

export type FilterOption = string;

export type Accumulator = Record<
  FilterType,
  Record<FilterOption, Set<Deal['id']>>
>;

export const DealScope = {
  Active: 'active',
  Archived: 'archived',
} as const;
export type DealScope = ValueOf<typeof DealScope>;

export const DefaultAccumulator: Accumulator = {
  status: {},
  delivery_preference: {},
  priority: {},
  finance_manager: {},
  sales_rep: {},
  funding_method: {},
  lender: {},
  rapid_recon_step: {},
  delivery_stage: {},
  delivery_queued: {},
  price_locked: {},
  traded_in: {},
  delivery_date: {},
  pickup_date: {},
  created_date: {},
  last_updated_date: {},
};

const useFilteredDeals = ({
  scope = DealScope.Active,
}: {
  scope: DealScope;
}): {
  accumulator: Accumulator;
} => {
  const { hubPathIdentifier } = useHubs();

  const deals =
    scope === DealScope.Archived
      ? dealStore.getArchivedDealsByHubPathId(hubPathIdentifier as string)
      : dealStore.getDealsByHubPathId(hubPathIdentifier as string);
  const { financeManagers, salesReps } = useUsers();
  const { lenders } = useLenders();

  const getFilterOptionsAndMatchFunctions = useCallback(
    (
      filterType: FilterType,
    ): {
      accumulatorFilterOptions: FilterOption[];
      matchFunction: (deal: Deal, filterOption: FilterOption) => boolean;
    } => {
      switch (filterType) {
        case 'status':
          return {
            accumulatorFilterOptions: Statuses,
            matchFunction: (deal, filterOption) => deal.status === filterOption,
          };
        case 'delivery_stage':
          return {
            accumulatorFilterOptions: DeliveryStages,
            matchFunction: (deal, filterOption) => {
              const coercedDeliveryStage = coerceDeliveryStage(
                deal.status,
                deal.delivery_stage ?? undefined,
              );

              return coercedDeliveryStage === filterOption;
            },
          };
        case 'priority':
          return {
            accumulatorFilterOptions: Priorities,
            matchFunction: (deal, filterOption) =>
              deal.priority === filterOption,
          };
        case 'delivery_preference':
          return {
            accumulatorFilterOptions: DeliveryPreferences,
            matchFunction: (deal, filterOption) =>
              deal.delivery_preference === filterOption,
          };
        case 'finance_manager':
          return {
            accumulatorFilterOptions: financeManagers.map(
              (manager: User) => manager.id,
            ),
            matchFunction: (deal, filterOption) =>
              deal.finance_manager?.id === filterOption,
          };
        case 'sales_rep':
          return {
            accumulatorFilterOptions: salesReps.map((rep: User) => rep.id),
            matchFunction: (deal, filterOption) =>
              deal.primary_sales_rep?.id === filterOption ||
              deal.secondary_sales_rep?.id === filterOption,
          };
        case 'funding_method':
          return {
            accumulatorFilterOptions: FundingMethods,
            matchFunction: (deal, filterOption) =>
              deal.funding_method === filterOption,
          };
        case 'lender':
          return {
            accumulatorFilterOptions: lenders.map((lender) => lender.id),
            matchFunction: (deal, filterOption) =>
              deal.lender?.id === filterOption,
          };
        case 'delivery_queued':
          return {
            accumulatorFilterOptions: ['queued', 'unqueued'],
            matchFunction: (deal, filterOption) => {
              if (filterOption === 'queued') {
                return !!deal.date_added_to_customer_delivery_queue;
              } else {
                return !deal.date_added_to_customer_delivery_queue;
              }
            },
          };
        case 'price_locked':
          return {
            accumulatorFilterOptions: ['locked', 'unlocked'],
            matchFunction: (deal, filterOption) => {
              if (!deal.price_breakdown) {
                return false;
              }

              if (filterOption === 'locked') {
                return deal.price_breakdown?.locked;
              } else {
                return !deal.price_breakdown?.locked;
              }
            },
          };
        case 'traded_in':
          return {
            accumulatorFilterOptions: ['yes', 'no'],
            matchFunction: (deal, filterOption) => {
              if (filterOption === 'yes') {
                return !isEmpty(deal.trades);
              } else {
                return isEmpty(deal.trades);
              }
            },
          };

        case 'delivery_date':
          return {
            accumulatorFilterOptions: [
              ...DELIVERY_DATE_FILTER_OPTIONS.map((option) => option.value),
              ...DELIVERY_DATE_FILTER_OPTIONS.map(
                (option) => `after-${option.value}`,
              ),
            ],
            matchFunction: (deal, filterOption) => {
              if (!deal.delivery_date) {
                return false;
              }

              const deliveryDate = dayjs(deal.delivery_date);

              if (filterOption === 'in-the-past') {
                const now = dayjs();
                return deliveryDate.isBefore(now);
              }
              if (filterOption === 'after-in-the-past') {
                const now = dayjs();
                return deliveryDate.isAfter(now);
              }

              if (filterOption === '1-day-from-now') {
                const tomorrow = dayjs().add(1, 'day').endOf('day');
                return deliveryDate.isBefore(tomorrow);
              }
              if (filterOption === 'after-1-day-from-now') {
                const tomorrow = dayjs().add(1, 'day').endOf('day');
                return deliveryDate.isAfter(tomorrow);
              }

              if (filterOption === '3-days-from-now') {
                const threeDaysFromNow = dayjs().add(3, 'day').endOf('day');
                return deliveryDate.isBefore(threeDaysFromNow);
              }
              if (filterOption === 'after-3-days-from-now') {
                const threeDaysFromNow = dayjs().add(3, 'day').endOf('day');
                return deliveryDate.isAfter(threeDaysFromNow);
              }

              if (filterOption === '5-days-from-now') {
                const fiveDaysFromNow = dayjs().add(5, 'day').endOf('day');
                return deliveryDate.isBefore(fiveDaysFromNow);
              }
              if (filterOption === 'after-5-days-from-now') {
                const fiveDaysFromNow = dayjs().add(5, 'day').endOf('day');
                return deliveryDate.isAfter(fiveDaysFromNow);
              }

              if (filterOption === '7-days-from-now') {
                const sevenDaysFromNow = dayjs().add(7, 'day').endOf('day');
                return deliveryDate.isBefore(sevenDaysFromNow);
              }
              if (filterOption === 'after-7-days-from-now') {
                const sevenDaysFromNow = dayjs().add(7, 'day').endOf('day');
                return deliveryDate.isAfter(sevenDaysFromNow);
              }

              if (filterOption === '14-days-from-now') {
                const fourteenDaysFromNow = dayjs().add(14, 'day').endOf('day');
                return deliveryDate.isBefore(fourteenDaysFromNow);
              }
              if (filterOption === 'after-14-days-from-now') {
                const fourteenDaysFromNow = dayjs().add(14, 'day').endOf('day');
                return deliveryDate.isAfter(fourteenDaysFromNow);
              }

              return false;
            },
          };
        case 'pickup_date':
          return {
            accumulatorFilterOptions: [
              ...PICKUP_DATE_FILTER_OPTIONS.map((option) => option.value),
              ...PICKUP_DATE_FILTER_OPTIONS.map(
                (option) => `after-${option.value}`,
              ),
            ],
            matchFunction: (deal, filterOption) => {
              if (!deal.pickup_date) {
                return false;
              }

              const pickupDate = dayjs(deal.pickup_date);

              if (filterOption === 'in-the-past') {
                const now = dayjs();
                return pickupDate.isBefore(now);
              }
              if (filterOption === 'after-in-the-past') {
                const now = dayjs();
                return pickupDate.isAfter(now);
              }

              if (filterOption === '1-day-from-now') {
                const tomorrow = dayjs().add(1, 'day').endOf('day');
                return pickupDate.isBefore(tomorrow);
              }
              if (filterOption === 'after-1-day-from-now') {
                const tomorrow = dayjs().add(1, 'day').endOf('day');
                return pickupDate.isAfter(tomorrow);
              }

              if (filterOption === '3-days-from-now') {
                const threeDaysFromNow = dayjs().add(3, 'day').endOf('day');
                return pickupDate.isBefore(threeDaysFromNow);
              }
              if (filterOption === 'after-3-days-from-now') {
                const threeDaysFromNow = dayjs().add(3, 'day').endOf('day');
                return pickupDate.isAfter(threeDaysFromNow);
              }

              if (filterOption === '5-days-from-now') {
                const fiveDaysFromNow = dayjs().add(5, 'day').endOf('day');
                return pickupDate.isBefore(fiveDaysFromNow);
              }
              if (filterOption === 'after-5-days-from-now') {
                const fiveDaysFromNow = dayjs().add(5, 'day').endOf('day');
                return pickupDate.isAfter(fiveDaysFromNow);
              }

              if (filterOption === '7-days-from-now') {
                const sevenDaysFromNow = dayjs().add(7, 'day').endOf('day');
                return pickupDate.isBefore(sevenDaysFromNow);
              }
              if (filterOption === 'after-7-days-from-now') {
                const sevenDaysFromNow = dayjs().add(7, 'day').endOf('day');
                return pickupDate.isAfter(sevenDaysFromNow);
              }

              if (filterOption === '14-days-from-now') {
                const fourteenDaysFromNow = dayjs().add(14, 'day').endOf('day');
                return pickupDate.isBefore(fourteenDaysFromNow);
              }
              if (filterOption === 'after-14-days-from-now') {
                const fourteenDaysFromNow = dayjs().add(14, 'day').endOf('day');
                return pickupDate.isAfter(fourteenDaysFromNow);
              }

              return false;
            },
          };
        case 'last_updated_date':
          return {
            accumulatorFilterOptions: [
              ...LAST_UPDATED_DATE_FILTER_OPTIONS.map((option) => option.value),
              ...LAST_UPDATED_DATE_FILTER_OPTIONS.map(
                (option) => `after-${option.value}`,
              ),
            ],
            matchFunction: (deal, filterOption) => {
              if (!deal.last_updated) {
                return false;
              }

              const lastUpdatedDate = dayjs(deal.last_updated);

              if (filterOption === '1-day-ago') {
                const now = dayjs().subtract(1, 'day').startOf('day');
                return lastUpdatedDate.isBefore(now);
              }
              if (filterOption === 'after-1-day-ago') {
                const now = dayjs().subtract(1, 'day').startOf('day');
                return lastUpdatedDate.isAfter(now);
              }

              if (filterOption === '3-days-ago') {
                const now = dayjs().subtract(3, 'day').startOf('day');
                return lastUpdatedDate.isBefore(now);
              }
              if (filterOption === 'after-3-days-ago') {
                const now = dayjs().subtract(3, 'day').startOf('day');
                return lastUpdatedDate.isAfter(now);
              }

              if (filterOption === '1-week-ago') {
                const now = dayjs().subtract(1, 'week').startOf('day');
                return lastUpdatedDate.isBefore(now);
              }
              if (filterOption === 'after-1-week-ago') {
                const now = dayjs().subtract(1, 'week').startOf('day');
                return lastUpdatedDate.isAfter(now);
              }

              if (filterOption === '1-month-ago') {
                const now = dayjs().subtract(1, 'month').startOf('day');
                return lastUpdatedDate.isBefore(now);
              }
              if (filterOption === 'after-1-month-ago') {
                const now = dayjs().subtract(1, 'month').startOf('day');
                return lastUpdatedDate.isAfter(now);
              }

              if (filterOption === '3-months-ago') {
                const now = dayjs().subtract(3, 'month').startOf('day');
                return lastUpdatedDate.isBefore(now);
              }
              if (filterOption === 'after-3-months-ago') {
                const now = dayjs().subtract(3, 'month').startOf('day');
                return lastUpdatedDate.isAfter(now);
              }

              if (filterOption === '6-months-ago') {
                const now = dayjs().subtract(6, 'month').startOf('day');
                return lastUpdatedDate.isBefore(now);
              }
              if (filterOption === 'after-6-months-ago') {
                const now = dayjs().subtract(6, 'month').startOf('day');
                return lastUpdatedDate.isAfter(now);
              }

              if (filterOption === '1-year-ago') {
                const now = dayjs().subtract(1, 'year').startOf('day');
                return lastUpdatedDate.isBefore(now);
              }
              if (filterOption === 'after-1-year-ago') {
                const now = dayjs().subtract(1, 'year').startOf('day');
                return lastUpdatedDate.isAfter(now);
              }

              return false;
            },
          };

        case 'created_date':
          return {
            accumulatorFilterOptions: [
              ...CREATED_DATE_FILTER_OPTIONS.map((option) => option.value),
              ...CREATED_DATE_FILTER_OPTIONS.map(
                (option) => `after-${option.value}`,
              ),
            ],
            matchFunction: (deal, filterOption) => {
              // TECH DEBT: we're using `last_updated` until BE gives us `created_at`
              if (!deal.created_at && !deal.last_updated) {
                return false;
              }

              const createdDate = dayjs(deal.created_at || deal.last_updated);

              if (filterOption === '1-day-ago') {
                const now = dayjs().subtract(1, 'day').startOf('day');
                return createdDate.isBefore(now);
              }
              if (filterOption === 'after-1-day-ago') {
                const now = dayjs().subtract(1, 'day').startOf('day');
                return createdDate.isAfter(now);
              }

              if (filterOption === '3-days-ago') {
                const now = dayjs().subtract(3, 'day').startOf('day');
                return createdDate.isBefore(now);
              }
              if (filterOption === 'after-3-days-ago') {
                const now = dayjs().subtract(3, 'day').startOf('day');
                return createdDate.isAfter(now);
              }

              if (filterOption === '1-week-ago') {
                const now = dayjs().subtract(1, 'week').startOf('day');
                return createdDate.isBefore(now);
              }
              if (filterOption === 'after-1-week-ago') {
                const now = dayjs().subtract(1, 'week').startOf('day');
                return createdDate.isAfter(now);
              }

              if (filterOption === '1-month-ago') {
                const now = dayjs().subtract(1, 'month').startOf('day');
                return createdDate.isBefore(now);
              }
              if (filterOption === 'after-1-month-ago') {
                const now = dayjs().subtract(1, 'month').startOf('day');
                return createdDate.isAfter(now);
              }

              if (filterOption === '3-months-ago') {
                const now = dayjs().subtract(3, 'month').startOf('day');
                return createdDate.isBefore(now);
              }
              if (filterOption === 'after-3-months-ago') {
                const now = dayjs().subtract(3, 'month').startOf('day');
                return createdDate.isAfter(now);
              }

              if (filterOption === '6-months-ago') {
                const now = dayjs().subtract(6, 'month').startOf('day');
                return createdDate.isBefore(now);
              }
              if (filterOption === 'after-6-months-ago') {
                const now = dayjs().subtract(6, 'month').startOf('day');
                return createdDate.isAfter(now);
              }

              if (filterOption === '1-year-ago') {
                const now = dayjs().subtract(1, 'year').startOf('day');
                return createdDate.isBefore(now);
              }
              if (filterOption === 'after-1-year-ago') {
                const now = dayjs().subtract(1, 'year').startOf('day');
                return createdDate.isAfter(now);
              }

              return false;
            },
          };

        default:
          return {
            accumulatorFilterOptions: [],
            matchFunction: () => false,
          };
      }
    },
    [
      JSON.stringify(financeManagers),
      JSON.stringify(salesReps),
      JSON.stringify(lenders),
    ],
  );

  const accumulator = useMemo(() => {
    const _accumulator: Accumulator = cloneDeep(DefaultAccumulator);

    for (const deal of deals) {
      for (const filterType of FILTER_TYPES) {
        const { accumulatorFilterOptions, matchFunction } =
          getFilterOptionsAndMatchFunctions(filterType);

        for (const option of accumulatorFilterOptions) {
          if (!_accumulator[filterType]) {
            _accumulator[filterType] = {};
          }

          if (!_accumulator[filterType][option]) {
            _accumulator[filterType][option] = new Set();
          }

          if (matchFunction(deal, option)) {
            _accumulator[filterType][option].add(deal.id);
          }
        }
      }
    }

    return _accumulator;
  }, [JSON.stringify(deals)]);

  return { accumulator };
};

export default useFilteredDeals;
